import { getFormErrorMessage } from "@core/utility/get-error-message";
import { ActivityFormRule, ActivityFormSchema } from "@factory/form/form.rule";
import { useFormDefaultActivity } from "@factory/form/form-default-activity.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseActivityModel } from "@factory/model/query-model-activity";
import { 
  useQyDeleteActivity, 
  useQyGetActivityById, 
  useQyUpdateActivity} from "@factory/query/activity.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditActivity() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultActivity();
  const navigate = useNavigate();
  const { mapUpdateActivity, mapDeleteActivity } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { activityId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Activity
  const handleGetApiSuccess = (data: GetResponseActivityModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("location", responseData?.location || "");
      setValue("activityType", responseData?.activity_type || "");
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("isChildActivity", Boolean(responseData?.is_child_activity));
      setValue("childRate", responseData?.child_rate);
      setValue("showAsOptional", Boolean(responseData?.show_as_optional));
      setValue("isFree", Boolean(responseData?.is_free));
      setValue("entranceFee", responseData?.entrance_fee);
  };
  const {
    data: activityResponse,
    isLoading,
    isError: activityError,
  } = useQyGetActivityById(activityId || "", handleGetApiSuccess);
  const activityData = activityResponse?.data?.[0];

  // API UPDATE Activity
  const handleUpdateApiSuccess = () => {
    showSuccess("Activity updated successfully");
  };
  const { mutate: updateActivity, isLoading: isEditLoading } = useQyUpdateActivity(
    handleUpdateApiSuccess
  );

  // API DELETE Activity
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Activity deleted successfully");
  };
  const { mutate: deleteActivity } = useQyDeleteActivity(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<ActivityFormSchema>({
    defaultValues: getDefault(activityData),
    resolver: zodResolver(ActivityFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: ActivityFormSchema) => {
    if (!activityData || !activityData.code) {
      throw new Error("No activity data");
    }

    const formData = mapUpdateActivity(form, activityData.code);
    updateActivity(formData);
  };
  const handleValidateError = (err: FieldErrors<ActivityFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!activityData || !activityData.code) {
      throw new Error("No activity data found");
    }

    const form = mapDeleteActivity(activityData.code);
    deleteActivity(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    activityId,
    activityData,
    isLoading,
    activityError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}